import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, PencilIcon, PlusIcon } from 'lucide-react';
import { SAMPLE_ROSTER } from '../../../data/academyAdmin';
import { canEditCourses, useAcademyHub } from '../../../context/AcademyHub';
import { Button } from '../../ui/Button';
import { CourseBuilder } from './CourseBuilder';
type Mode = {
  kind: 'list';
} | {
  kind: 'new';
} | {
  kind: 'edit';
  slug: string;
};
export function AdminCourses() {
  const {
    courses,
    isPublished,
    setPublished,
    role,
    hub
  } = useAcademyHub();
  const [mode, setMode] = React.useState<Mode>({
    kind: 'list'
  });
  const [message, setMessage] = React.useState('');
  const isAdmin = role === 'admin';
  const canEdit = canEditCourses(role);
  const done = (text: string) => {
    setMessage(text);
    setMode({
      kind: 'list'
    });
    window.scrollTo({
      top: 0
    });
  };
  if (mode.kind === 'new' && isAdmin) {
    return <CourseBuilder onDone={done} onCancel={() => setMode({
      kind: 'list'
    })} data-id="element-130" data-mp="2st7yfhmwdfa5" />;
  }
  if (mode.kind === 'edit' && canEdit) {
    const course = courses.find(c => c.slug === mode.slug);
    if (course) return <CourseBuilder key={course.slug} course={course} onDone={done} onCancel={() => setMode({
      kind: 'list'
    })} data-id="element-131" data-mp="20ohlrep11y09" />;
  }
  return <>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-132" data-mp="1mymhuv51i1te">
        <p className="max-w-xl text-[14px] text-body" data-id="element-133" data-mp="1rxhaspwq16r8">
          {isAdmin ? 'Build new courses, edit content and choose what learners can see.' : 'Edit course content. Enrolled learners are notified when you publish an update.'}
        </p>
        {isAdmin && <Button type="button" onClick={() => {
        setMessage('');
        setMode({
          kind: 'new'
        });
      }} className="shrink-0" data-id="element-134" data-mp="25om2n5tukur0">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-135" data-mp="2aluk14768ur7" />
            New course
          </Button>}
      </div>

      {message && <p role="status" className="mt-5 flex items-start gap-2 rounded-sm border border-line bg-panel px-4 py-3 text-[14px] text-ink" data-id="element-136" data-mp="ub0f1p3mz17l">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#4ADE80]" aria-hidden="true" data-id="element-137" data-mp="f5w15q3ig302" />
          {message}
        </p>}

      <div className="mt-6 overflow-x-auto rounded-md border border-line" data-id="element-138" data-mp="b4kik4j20i1d">
        <table className="w-full min-w-[760px] text-left text-[14px]" data-id="element-139" data-mp="6came6rcilqn">
          <thead className="bg-panel text-small text-muted" data-id="element-140" data-mp="1ehdh5ynrdmo">
            <tr data-id="element-141" data-mp="1c50z8bo5vh0">
              <th scope="col" className="px-4 py-3 font-medium" data-id="element-142" data-mp="1cep0qd5lsk2">Course</th>
              <th scope="col" className="px-4 py-3 font-medium" data-id="element-143" data-mp="3slu0w2q4wnb1">Content</th>
              <th scope="col" className="px-4 py-3 font-medium" data-id="element-144" data-mp="3nko5fx53alxt">Enrolled</th>
              <th scope="col" className="px-4 py-3 font-medium" data-id="element-145" data-mp="3jj2jznehozc0">Status</th>
              <th scope="col" className="px-4 py-3" data-id="element-146" data-mp="3ekrchjbo30wy"><span className="sr-only" data-id="element-147" data-mp="3elswweekozrc">Actions</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line" data-id="element-148" data-mp="1hsr64c9xnojb">
            {courses.map(c => {
            const published = isPublished(c.slug);
            const lessons = c.modules.reduce((n, m) => n + m.lessons.length, 0);
            const quizzes = c.modules.filter(m => m.quiz?.length).length;
            const enrolled = SAMPLE_ROSTER.filter(l => c.slug in l.enrolled).length;
            const update = hub.courseUpdates[c.slug];
            return <tr key={c.slug} data-id="element-149" data-mp="1z1tnyxnabm8e">
                  <td className="px-4 py-3" data-id="element-150" data-mp="1z0s17vwwpbau">
                    <Link to={`/academy/courses/${c.slug}`} className="block text-ink hover:text-royal" data-id="element-151" data-mp="1z3wgppftntgt">
                      {c.title}
                    </Link>
                    <span className="text-small text-muted" data-id="element-152" data-mp="16dxj35nkao0j">
                      {c.difficulty} · {c.hours} h{c.custom ? ' · built here' : ''}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-small text-body" data-id="element-153" data-mp="1fevsy4kolpr1">
                    {c.modules.length} modules · {lessons} lessons · {quizzes} quizzes
                    {update && <span className="block text-muted" data-id="element-154" data-mp="2qsps99va4ik2">Version {update.version}, updated by {update.by}</span>}
                  </td>
                  <td className="px-4 py-3 text-ink" data-id="element-155" data-mp="2pcvjq2gcu7w7">{enrolled}</td>
                  <td className="px-4 py-3" data-id="element-156" data-mp="2kczc7t8g7ybw">
                    {isAdmin ? <div className="flex items-center gap-2.5" data-id="element-157" data-mp="2kf3mcswxxtkm">
                        <button type="button" role="switch" aria-checked={published} aria-label={`${published ? 'Unpublish' : 'Publish'} ${c.title}`} onClick={() => setPublished(c.slug, !published)} className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-150 ${published ? 'bg-gold' : 'bg-line'}`} data-id="element-158" data-mp="2zbkz9oks3g9a">
                          <span className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-ink transition-transform duration-150 ease-out ${published ? 'translate-x-[22px]' : 'translate-x-0.5'}`} data-id="element-159" data-mp="3d26kvl1ma4av" />
                        </button>
                        <span className="text-small text-body" data-id="element-160" data-mp="9js73evr0z5r">{published ? 'Published' : 'Draft'}</span>
                      </div> : <span className={`text-small ${published ? 'text-ink' : 'text-muted'}`} data-id="element-161" data-mp="i2gucdls69k">{published ? 'Published' : 'Draft'}</span>}
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-162" data-mp="5iq9t3le6x6f">
                    {canEdit && <button type="button" onClick={() => {
                  setMessage('');
                  setMode({
                    kind: 'edit',
                    slug: c.slug
                  });
                }} className="inline-flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-small text-royal hover:bg-panel hover:text-ink" data-id="element-163" data-mp="3mvk8v8g43cj0">
                        <PencilIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-164" data-mp="33xzmq94qes23" />
                        Edit content
                      </button>}
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-small text-muted" data-id="element-165" data-mp="19qxeqh9h2xtp">
        Unpublished courses disappear from the catalogue. Learners already enrolled keep access and their progress. Video upload and the quiz builder
        arrive with the Academy server.
      </p>
    </>;
}